import type { NewListingSource } from 'app/types/listing';

export interface HubDecision {
  id: string;
  roundId: string;
  suggestedListingId: string;
  title: string;
  picture: string;
  kind: 'accept' | 'reject';
  source?: NewListingSource;
  status: 'pending' | 'saved' | 'failed';
  attempts: number;
  retryAt?: number;
  error?: string;
}
export interface HubQueueSnapshot {
  ready: boolean;
  storageError: boolean;
  decisions: HubDecision[];
}
interface QueueDependencies {
  read: () => Promise<string | null>;
  write: (data: string) => Promise<void>;
  send: (decision: HubDecision, signal: AbortSignal) => Promise<void>;
  onSaved: (decision: HubDecision) => void;
  classifyError: (error: unknown) => 'auth' | 'retry' | 'permanent';
}

/** Screen-independent outbox. Persist before sending; one request at a time. */
export class OfferHubQueue {
  private snapshot: HubQueueSnapshot = { ready: false, storageError: false, decisions: [] };
  private listeners = new Set<() => void>();
  private writes: Promise<void> = Promise.resolve();
  private active = true;
  private isActive() {
    return this.active;
  }
  private sending = false;
  private paused = false;
  private timer?: ReturnType<typeof setTimeout>;
  private controller?: AbortController;
  constructor(private deps: QueueDependencies) {}
  getSnapshot = () => this.snapshot;
  subscribe = (listener: () => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };
  private update(patch: Partial<HubQueueSnapshot>) {
    this.snapshot = { ...this.snapshot, ...patch };
    this.listeners.forEach((listener) => listener());
  }
  async start() {
    this.active = true;
    try {
      const raw = await this.deps.read();
      const stored = raw ? (JSON.parse(raw) as { version: number; decisions: HubDecision[] }) : undefined;
      if (stored && (stored.version !== 2 || !Array.isArray(stored.decisions))) throw new Error('Unsupported outbox');
      if (!this.isActive()) return;
      this.update({ ready: true, storageError: false, decisions: stored?.decisions ?? [] });
      void this.drain();
    } catch {
      if (this.isActive()) this.update({ storageError: true });
    }
  }
  stop() {
    this.active = false;
    clearTimeout(this.timer);
    this.controller?.abort();
    this.update({});
  }
  enqueue(decision: HubDecision) {
    if (
      !this.isActive() ||
      !this.snapshot.ready ||
      this.snapshot.decisions.some((entry) => entry.suggestedListingId === decision.suggestedListingId)
    )
      return false;
    this.update({ decisions: [...this.snapshot.decisions, decision] });
    void this.persist().then(() => this.drain());
    return true;
  }
  retry(id?: string) {
    if (!this.snapshot.ready) {
      void this.start();
      return;
    }
    this.paused = false;
    this.update({
      decisions: this.snapshot.decisions.map((entry) =>
        entry.status !== 'saved' && (!id || entry.id === id)
          ? { ...entry, status: 'pending', error: undefined, retryAt: undefined }
          : entry,
      ),
    });
    void this.persist().then(() => this.drain());
  }
  clearErrors() {
    if (!this.snapshot.ready) return;
    const decisions = this.snapshot.decisions.filter((entry) => entry.status !== 'failed');
    if (decisions.length === this.snapshot.decisions.length) return;
    this.update({ decisions });
    void this.persist();
  }
  wake = () => {
    if (!this.isActive()) return;
    this.paused = false;
    this.update({
      decisions: this.snapshot.decisions.map((entry) =>
        entry.status === 'pending' ? { ...entry, retryAt: undefined } : entry,
      ),
    });
    void this.drain();
  };
  // Wait briefly for server answers, but never block browsing on an unavailable outbox.
  whenSettled = (timeout = 5000) => {
    if (!this.isActive() || !this.snapshot.decisions.some((entry) => entry.status === 'pending'))
      return Promise.resolve();
    return new Promise<void>((resolve) => {
      const finish = () => {
        clearTimeout(expiry);
        unsubscribe();
        resolve();
      };
      const unsubscribe = this.subscribe(() => {
        if (!this.isActive() || this.paused || !this.snapshot.decisions.some((entry) => entry.status === 'pending'))
          finish();
      });
      const expiry = setTimeout(finish, timeout);
      this.wake();
    });
  };
  private persist(): Promise<boolean> {
    let succeeded = false;
    this.writes = this.writes
      .catch(() => undefined)
      .then(async () => {
        try {
          await this.deps.write(JSON.stringify({ version: 2, decisions: this.snapshot.decisions }));
          succeeded = true;
          this.update({ storageError: false });
        } catch {
          this.update({ storageError: true });
        }
      });
    return this.writes.then(() => succeeded);
  }
  private replace(id: string, patch: Partial<HubDecision>) {
    this.update({
      decisions: this.snapshot.decisions.map((entry) => (entry.id === id ? { ...entry, ...patch } : entry)),
    });
  }
  private async drain() {
    if (!this.isActive() || !this.snapshot.ready || this.sending || this.paused) return;
    this.sending = true;
    clearTimeout(this.timer);
    try {
      // Serializes against enqueue writes, including when the previous request completes.
      if (!(await this.persist()) || !this.isActive()) return;
      while (this.isActive() && !this.paused) {
        const pending = this.snapshot.decisions.filter((entry) => entry.status === 'pending');
        const decision = pending.find((entry) => !entry.retryAt || entry.retryAt <= Date.now());
        if (!decision) {
          const next = Math.min(...pending.map((entry) => entry.retryAt ?? Infinity));
          if (Number.isFinite(next))
            this.timer = setTimeout(
              () => {
                void this.drain();
              },
              Math.max(0, next - Date.now()),
            );
          return;
        }
        if (!(await this.persist()) || !this.isActive()) return;
        this.controller = new AbortController();
        try {
          await this.deps.send(decision, this.controller.signal);
          if (!this.isActive()) return;
          const saved: HubDecision = {
            ...decision,
            status: 'saved',
            error: undefined,
          };
          this.replace(decision.id, saved);
          if (!(await this.persist()) || !this.isActive()) return;
          // A cache/analytics failure must not turn a confirmed write into another request.
          try {
            this.deps.onSaved(saved);
          } catch {
            /* The server result remains saved. */
          }
        } catch (error) {
          if (!this.isActive()) return;
          const kind = this.deps.classifyError(error);
          const attempts = decision.attempts + 1;
          if (kind === 'auth') this.paused = true;
          this.replace(decision.id, {
            attempts,
            status: kind === 'permanent' ? 'failed' : 'pending',
            error: error instanceof Error ? error.message : String(error),
            retryAt: kind === 'retry' ? Date.now() + Math.min(60000, 1000 * 2 ** Math.min(attempts, 6)) : undefined,
          });
          if (!(await this.persist())) return;
        }
      }
    } finally {
      this.sending = false;
    }
  }
}
